import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DataService } from './data.service';

export interface LiveNotification {
  id: number;
  audience: string;
  title: string;
  message: string;
  link: string;
  isRead: boolean | number;
  createdAt: string;
  eventKey: string;
}

@Injectable({ providedIn: 'root' })
export class RealtimeNotificationService implements OnDestroy {
  readonly items$ = new BehaviorSubject<LiveNotification[]>([]);
  readonly unread$ = new BehaviorSubject<number>(0);

  private running = false;
  private generation = 0;
  private sinceId = 0;
  private audience: 'admin' | 'subscriber' = 'admin';
  private subscriberId: number | null = null;

  constructor(private data: DataService) {}

  start(audience: 'admin' | 'subscriber', subscriberId?: number | null) {
    const nextSubscriber = audience === 'subscriber' ? Number(subscriberId) || null : null;
    if (this.running && this.audience === audience && this.subscriberId === nextSubscriber) {
      return;
    }
    this.stop();
    this.audience = audience;
    this.subscriberId = nextSubscriber;
    this.sinceId = 0;
    this.running = true;
    const generation = ++this.generation;
    this.load(generation);
  }

  stop() {
    this.running = false;
    this.generation++;
  }

  ngOnDestroy() {
    this.stop();
  }

  markRead(item: LiveNotification) {
    if (!item || this.isRead(item)) {
      return;
    }
    item.isRead = true;
    this.publish(this.items$.value);
    this.data.getUserData('admin/notifications/markRead', this.body({ id: item.id }), this.data.getUserToken())
      .subscribe(() => {}, () => {});
  }

  markAllRead() {
    const next = this.items$.value.map(item => ({ ...item, isRead: true }));
    this.publish(next);
    this.data.getUserData('admin/notifications/markAllRead', this.body(), this.data.getUserToken())
      .subscribe(() => {}, () => {});
  }

  isRead(item: LiveNotification): boolean {
    return item.isRead === true || item.isRead === 1;
  }

  private load(generation: number) {
    if (!this.running || generation !== this.generation) {
      return;
    }
    this.data.getUserData('admin/notifications/list', this.body(), this.data.getUserToken())
      .subscribe((res: any) => {
        if (!this.running || generation !== this.generation) {
          return;
        }
        const rows = this.rowsFrom(res);
        this.publish(rows);
        this.sinceId = rows.reduce((max, row) => Math.max(max, Number(row.id) || 0), this.sinceId);
        this.wait(generation);
      }, () => this.retry(generation, () => this.load(generation)));
  }

  private wait(generation: number) {
    if (!this.running || generation !== this.generation) {
      return;
    }
    this.data.getUserData('admin/notifications/wait', this.body({ sinceId: this.sinceId }), this.data.getUserToken())
      .subscribe((res: any) => {
        if (!this.running || generation !== this.generation) {
          return;
        }
        const rows = this.rowsFrom(res);
        if (rows.length) {
          const byId = new Map<number, LiveNotification>();
          rows.concat(this.items$.value).forEach(row => byId.set(Number(row.id), row));
          const merged = Array.from(byId.values()).sort((a, b) => Number(b.id) - Number(a.id)).slice(0, 40);
          this.publish(merged);
          this.sinceId = merged.reduce((max, row) => Math.max(max, Number(row.id) || 0), this.sinceId);
        }
        this.wait(generation);
      }, () => this.retry(generation, () => this.wait(generation)));
  }

  private retry(generation: number, again: () => void) {
    setTimeout(() => {
      if (this.running && generation === this.generation) {
        again();
      }
    }, 4000);
  }

  private publish(rows: LiveNotification[]) {
    this.items$.next(rows);
    this.unread$.next(rows.filter(row => !this.isRead(row)).length);
  }

  private rowsFrom(res: any): LiveNotification[] {
    return Array.isArray(res && res.Data) ? res.Data : [];
  }

  private body(extra: any = {}) {
    return {
      audience: this.audience,
      subscriberId: this.subscriberId,
      ...extra
    };
  }
}
